<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
import CaretAnchoredPicker from 'dashboard/components-next/preview-picker/CaretAnchoredPicker.vue';
import GroupsAPI from 'dashboard/api/masifi/masifiWhatsappQrGroups';
import {
  isMasifiWaGroupChat,
  masifiWaMentionText,
} from 'dashboard/helper/masifi/masifiWaGroups';
import { WHATSAPP_QR_FEATURE } from 'dashboard/helper/masifi/masifiWhatsappQr';

// "@" en la respuesta pública de un grupo de WhatsApp QR (spec F3 §6.1, ruling 7): sugiere participantes (búsqueda en
// el servidor) y, para un administrador (F3a), "@todos". Inserta TEXTO ("@Nombre "): el backend lo convierte en la
// mención de WhatsApp al enviar. Montado en Editor.vue; fuera de un grupo no dibuja nada.
const props = defineProps({
  conversationId: { type: Number, default: null },
  caretPosition: { type: Object, default: null },
  searchKey: { type: String, default: '' },
});
const emit = defineEmits(['select', 'close', 'removeTrigger']);

const { t, tm } = useI18n();
const { isAdmin } = useAdmin();
const getConversation = useMapGetter('getConversationById');
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');

// Final review A-m1: module off (owner entitlement or admin toggle) = no picker either — clean disable, same flag as the
// panel and the Individuales/Grupos filter.
const isGroup = computed(
  () =>
    isFeatureEnabled.value(accountId.value, WHATSAPP_QR_FEATURE) &&
    isMasifiWaGroupChat(getConversation.value?.(props.conversationId))
);
const searchQuery = ref(props.searchKey);
const members = ref([]);
const isLoading = ref(false);

const fetchMembers = debounce(async query => {
  try {
    const { data } = await GroupsAPI.members(props.conversationId, {
      q: query,
    });
    members.value = data.payload.filter(member => !member.our_number);
  } catch (error) {
    members.value = [];
  } finally {
    isLoading.value = false;
  }
}, 250);

watch(
  [isGroup, searchQuery],
  ([group, query]) => {
    if (!group) return;
    isLoading.value = true;
    fetchMembers(query);
  },
  // sync: la búsqueda se agenda en el mismo tick del tipeo (el debounce ya agrupa las teclas).
  { immediate: true, flush: 'sync' }
);

// tm (crudo), no t: "@todos" empieza con "@", que el compilador de vue-i18n toma como mensaje enlazado.
const allToken = computed(() =>
  tm('MASIFI_WHATSAPP_QR.GROUPS.MENTIONS.ALL_TOKEN')
);
const items = computed(() => {
  const list = members.value.map(member => ({
    id: `wa-${member.id}`,
    record: member,
    label: member.name,
    title: member.name,
    subtitle: member.phone ? `+${member.phone}` : '',
  }));
  const query = searchQuery.value.trim().toLowerCase();
  if (isAdmin.value && allToken.value.toLowerCase().includes(query)) {
    list.unshift({
      id: 'wa-all',
      record: { all: true },
      label: allToken.value,
      title: allToken.value,
      subtitle: t('MASIFI_WHATSAPP_QR.GROUPS.MENTIONS.ALL_HELP'),
    });
  }
  return list;
});

const onSelect = item =>
  emit(
    'select',
    item.record.all
      ? `${allToken.value} `
      : masifiWaMentionText(item.record, members.value)
  );
</script>

<template>
  <CaretAnchoredPicker
    v-if="isGroup"
    v-model:search="searchQuery"
    :caret-position="caretPosition"
    :items="items"
    :is-loading="isLoading"
    :search-placeholder="t('MASIFI_WHATSAPP_QR.GROUPS.MENTIONS.SEARCH')"
    :empty-label="t('COMBOBOX.EMPTY_STATE')"
    @select="onSelect"
    @close="emit('close')"
    @remove-trigger="emit('removeTrigger')"
  />
</template>
